import React, { ReactNode } from 'react'

import LogicStatement from './LogicStatement'

import { IActionRulesSubLogicStatements } from '../../../actions/types'
import { IPreviewOptions } from '../types'

import { transformText, escapeText, unescapeText } from './escape'

import { LOGIC_END_RE, LOGIC_START_RE, HIDE_TEXT } from './const'
import useInterfaceLocale from '../../locale/useInterfaceLocale'

import commonStyle from '../../../questions/_common/style.module.scss'

export interface TextWithOptionsProps {
  text: string
  options: IPreviewOptions
  rules?: IActionRulesSubLogicStatements
}

const TextWithOptions = React.memo<TextWithOptionsProps>(
  ({ text, options, rules = {} }) => {
    const escapedText = escapeText(text)
    const { localeOptions } = useInterfaceLocale()
    if (!options.showHidden) {
      const hideTextKey = Object.keys(rules).find(key => key.toLowerCase() === HIDE_TEXT)
      if (hideTextKey && rules[hideTextKey]?.condition) {
        return null
      }
    }

    if (!options.showLogic) {
      // replace logic with values
      const content = (
        <span
          dangerouslySetInnerHTML={{
            __html: unescapeText(
              escapedText.replace(new RegExp(`${LOGIC_START_RE}[^${LOGIC_END_RE}]*${LOGIC_END_RE}`, 'g'), match =>
                rules?.[match]?.condition ? transformText(rules[match].text) || '' : ''
              )
            )
          }}
          data-testid="SW-Text"
        />
      )
      return localeOptions.textDir ? <div className={commonStyle.rtlContent}>{content}</div> : content
    } else {
      // render logic as components, return array of ReactNodes
      const result: ReactNode[] = []
      const re = new RegExp(`${LOGIC_START_RE}[^${LOGIC_END_RE}]*${LOGIC_END_RE}`, 'g')
      let match = re.exec(escapedText)
      let position = 0
      if (match) {
        while (match) {
          const logicStatement = match[0]
          const logicStart = match.index
          const logicEnd = logicStart + logicStatement.length
          const textBefore = escapedText.slice(position, logicStart)
          const textAfter = escapedText.slice(logicEnd)
          if (textBefore) {
            result.push(<span key={position} dangerouslySetInnerHTML={{ __html: unescapeText(textBefore) }}></span>)
          }
          result.push(
            <LogicStatement
              key={logicStart}
              statement={unescapeText(logicStatement)}
              value={rules?.[logicStatement] || {}}
            />
          )
          position = logicEnd
          match = re.exec(escapedText)
          if (!match && textAfter) {
            result.push(<span key={logicEnd} dangerouslySetInnerHTML={{ __html: unescapeText(textAfter) }}></span>)
          }
        }
      } else {
        result.push(
          <span
            key={position}
            dangerouslySetInnerHTML={{ __html: unescapeText(escapedText) }}
            data-testid="SW-Text"
          ></span>
        )
      }
      return localeOptions.textDir ? (
        <div className={commonStyle.rtlContent}>{result}</div>
      ) : (
        <React.Fragment>{result}</React.Fragment>
      )
    }
  },
  (prevProps, nextProps) =>
    prevProps.text === nextProps.text && prevProps.rules === nextProps.rules && prevProps.options === nextProps.options
)

TextWithOptions.displayName = 'TextWithOptions'

export default TextWithOptions
